/**
 * A precompile pass that actually covers the whole scene.
 *
 * `WebGPURenderer.compileAsync(scene, camera)` sounds like "compile every
 * material in this scene", and its own doc comment says so, but its render
 * list is built by `Renderer._projectObject` — the same walk `render()` uses,
 * with the same two exits: `object.visible === false` returns immediately, and
 * a mesh with `frustumCulled` left at its default is skipped unless it
 * intersects *this camera's frustum right now*. Anything behind you at the
 * moment you call it is therefore not precompiled at all.
 *
 * That is invisible in most three.js scenes, because a pipeline is cached per
 * *material*: the one object of a given material that happens to face you pays
 * the compile and every sibling reuses it. It is not invisible here.
 * `RenderObject.getMaterialCacheKey()` in three r185 appends `object.uuid` for
 * any `InstancedMesh` (an upstream `TODO`, and the subject of the 2026-08-19
 * world-stability entry in `lessons.md`), so a region's ~40 instanced meshes
 * are ~40 separate pipelines even though they share one material. Precompiling
 * only the ones in front of the aeroplane leaves the rest to compile
 * synchronously, mid-flight, on the frame each first enters the frustum —
 * which is to say: on a turn (measured at 2–27 ms per such frame against a
 * 0.8 ms median, perf lane, 2026-08-23).
 *
 * The fix is to lift both exits for the duration of the compile and put them
 * back afterwards. Nothing is drawn — `compileAsync` builds pipelines and
 * never submits a pass — so forcing `visible` costs a picture nothing, and it
 * is what reaches the sprite pools, which sit at `visible = false` until their
 * first puff (`three/screen-sprites.ts`).
 */

import type * as THREE from "three/webgpu";

/** One object's saved flags, held only for the length of a precompile pass. */
interface SavedFlags {
  readonly object: THREE.Object3D;
  readonly visible: boolean;
  readonly frustumCulled: boolean;
}

/**
 * Clears `visible`/`frustumCulled` over the whole subtree and returns what to
 * hand `restoreSceneFlags`. Exported for its test; callers want
 * `precompileScene`.
 */
export function forceSceneFlags(root: THREE.Object3D): readonly SavedFlags[] {
  const saved: SavedFlags[] = [];
  root.traverse((object) => {
    if (object.visible && object.frustumCulled === false) return;
    saved.push({ object, visible: object.visible, frustumCulled: object.frustumCulled });
    object.visible = true;
    object.frustumCulled = false;
  });
  return saved;
}

/** Puts back exactly what `forceSceneFlags` took, in any order. */
export function restoreSceneFlags(saved: readonly SavedFlags[]): void {
  for (const entry of saved) {
    entry.object.visible = entry.visible;
    entry.object.frustumCulled = entry.frustumCulled;
  }
}

/**
 * Precompiles every pipeline the scene can ever need from `camera`, not merely
 * the ones currently on screen. `finally` restores the flags even if the
 * compile rejects, so a failed precompile costs a stutter and never a scene
 * with everything switched on.
 */
export async function precompileScene(
  renderer: THREE.WebGPURenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
): Promise<void> {
  const saved = forceSceneFlags(scene);
  try {
    await renderer.compileAsync(scene, camera);
  } finally {
    restoreSceneFlags(saved);
  }
}
